import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismLoadingBar,
  PrismModal,
  PrismRadioGroup,
  PrismToggle,
} from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import type { CentralMappingOrganizationSettings } from 'lumen/models';
import { PMS_VALUE_TYPES } from 'lumen/utils/central-mapping-constants';
import { localCopy } from 'tracked-toolbox';

type SegmentationView = 'market_code' | 'rate_code';

interface SettingsMappingModalsSegmentationViewModalSignature {
  Args: {
    onCloseModal: () => void;
    onSaved?: () => void;
    settings: CentralMappingOrganizationSettings;
  };
}

export default class SettingsMappingModalsSegmentationViewModal extends Component<SettingsMappingModalsSegmentationViewModalSignature> {
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @localCopy('args.settings.mappingSegmentationView')
  declare mappingSegmentationView: SegmentationView | null;

  @localCopy('args.settings.ratePlanFallbackEnabled')
  declare ratePlanFallbackEnabled: boolean;

  saveTask = task({ drop: true }, async (): Promise<void> => {
    const { settings } = this.args;
    const segmentationView = this.selectedSegmentationView;

    // Fallback is only meaningful under rate_code; reset it when switching away.
    const fallback = this.isRateCode ? this.ratePlanFallbackEnabled : false;
    const segmentationChanged =
      segmentationView !== settings.mappingSegmentationView;
    const fallbackChanged = fallback !== settings.ratePlanFallbackEnabled;

    // Mutate up-front so EmberData's PATCH picks these up via `changedAttributes()`.
    settings.mappingSegmentationView = segmentationView;
    settings.ratePlanFallbackEnabled = fallback;

    try {
      await settings.save({ adapterOptions: { usePatch: true } });
    } catch (error) {
      settings.rollbackAttributes();
      this.toast.error(this.intl.t('general.error'), { showIcon: true });
      this.errorTracking.captureException(error);
      this.args.onCloseModal();
      return;
    }

    if (segmentationChanged) {
      this.usageAnalytics.track('Segment View Chosen', {
        eventTrigger: 'segment-type',
        segmentType: segmentationView,
      });
    }
    if (fallbackChanged) {
      this.usageAnalytics.track('Fallback Rule Chosen', {
        eventTrigger: 'fallback-rule',
        enabled: fallback,
      });
    }

    this.toast.success(this.intl.t('general.changes_saved.body'), {
      timeout: TOAST_TIMEOUT_LONG,
      showIcon: true,
    });

    this.args.onSaved?.();
    this.args.onCloseModal();
  });

  get hasChanges(): boolean {
    const { settings } = this.args;
    // Null on a fresh record means the user hasn't picked yet — don't treat
    // the default Market code as a change or Apply would persist a phantom write.
    const userHasPickedSegmentation = this.mappingSegmentationView !== null;

    const segmentationChanged =
      userHasPickedSegmentation &&
      this.mappingSegmentationView !== settings.mappingSegmentationView;

    const fallbackChanged =
      this.isRateCode &&
      this.ratePlanFallbackEnabled !== settings.ratePlanFallbackEnabled;

    return segmentationChanged || fallbackChanged;
  }

  get isRateCode(): boolean {
    return this.selectedSegmentationView === PMS_VALUE_TYPES.RATE_CODE;
  }

  get selectedSegmentationView(): SegmentationView {
    return this.mappingSegmentationView ?? PMS_VALUE_TYPES.MARKET_CODE;
  }

  @action
  setMappingSegmentationView(event: Event): void {
    this.mappingSegmentationView = (event.target as HTMLInputElement)
      ?.value as SegmentationView;
  }

  @action
  setRatePlanFallbackEnabled(isChecked: boolean): void {
    this.ratePlanFallbackEnabled = isChecked;
  }

  <template>
    <PrismModal
      @onClose={{@onCloseModal}}
      @title={{t
        "settings.central_mapping.temporary_mapping_type_settings.title"
      }}
    >
      <div
        class="modal--small modal--padded settings-modal__content"
        data-test-id="segmentation-view-modal"
      >
        {{#if this.saveTask.isRunning}}
          <PrismLoadingBar />
        {{/if}}

        <p class="settings-modal__text paragraph-300--regular">
          {{t "settings.central_mapping.temporary_mapping_type_settings.body"}}
        </p>

        <PrismRadioGroup
          @isHorizontal={{true}}
          @name="segmentation-view-radio-group"
          @onChange={{this.setMappingSegmentationView}}
          @value={{this.selectedSegmentationView}}
          as |RadioGroup|
        >
          <RadioGroup.input
            @value={{PMS_VALUE_TYPES.MARKET_CODE}}
            data-test-id="segmentation-view-modal-market-code-radio"
          >
            {{t "settings.pms_mapping.market_code"}}
          </RadioGroup.input>
          <RadioGroup.input
            @value={{PMS_VALUE_TYPES.RATE_CODE}}
            data-test-id="segmentation-view-modal-rate-code-radio"
          >
            {{t "general.rate_code"}}
          </RadioGroup.input>
        </PrismRadioGroup>

        {{#if this.isRateCode}}
          <div
            class="rounded-200 bg-neutral-subdued mt-300 flex flex-col gap-100 p-400"
            data-test-id="segmentation-view-modal-fallback"
          >
            <PrismToggle
              @dataTestId="segmentation-view-modal-fallback-toggle"
              @isChecked={{this.ratePlanFallbackEnabled}}
              @label={{t
                "settings.central_mapping.segmentation_view_modal.fallback_rule_label"
              }}
              @onChange={{this.setRatePlanFallbackEnabled}}
            />
            <p class="paragraph-200--regular text-neutral-subdued">
              {{t
                "settings.central_mapping.segmentation_view_modal.fallback_rule_description"
              }}
            </p>
          </div>
        {{/if}}
      </div>

      <div class="settings-modal__footer">
        <GeneralFormFieldsCancelAndConfirm
          @cancel={{@onCloseModal}}
          @confirm={{this.saveTask.perform}}
          @confirmText={{t "general.apply"}}
          @isConfirmDisabled={{not this.hasChanges}}
        />
      </div>
    </PrismModal>
  </template>
}
